<script setup lang="ts">
import { useRoute } from 'vue-router';
import type { TreeNode } from '../types';

const route = useRoute();
const media = useMedia();
const { $events } = useEvents();
const { $alert } = useAlerts();
const { $t } = useLocalization();

const props = defineProps<{
    node: TreeNode;
    pages: TreeNode[];
    expanded: (number | string)[];
    menu: null | ((node: TreeNode) => Promise<any[]>);
    active: null | ((node: TreeNode) => boolean);
    move: (e: any) => boolean;
    disabled?: boolean;
}>();

const emit = defineEmits<{
    (e: 'toggle', node: TreeNode): void;
    (e: 'click', node: TreeNode): void;
    (e: 'sort', pages: TreeNode[]): void;
}>();

const hover = ref(false);
const contextMenu = ref(false);
const menuItems = ref([]);

const hasChildren = computed(() => props.node.hasChildren);
const hasMenu = computed(() => props.menu != null);
const isExpanded = computed(() => hasChildren.value && props.expanded.indexOf(props.node.id) >= 0);
const isCollapsed = computed(() => hasChildren.value && props.expanded.indexOf(props.node.id) === -1);

const isActive = computed(() =>
{
    if (props.active != null && typeof props.active === 'function')
        return props.active(props.node);
    else
        return route.params.id === props.node.id.toString();
});

const container = computed(() => `node-container-${props.node.id}`);

const hidePopover = (node: TreeNode): void =>
{
    if (node === null || node != props.node)
    {
        menuItems.value = [];
        contextMenu.value = false;
        hover.value = false;
    }
};

onMounted(() =>
{
    $events.$on('tree::popover::hide', hidePopover);
});

onBeforeUnmount(() =>
{
    $events.$off('tree::popover::hide', hidePopover);
});

const getMenu = async (node: TreeNode): Promise<any[]> =>
{
    if (props.menu)
        return await props.menu(node);
    else
        return [];
};

const icon = (icon: string): string =>
{
    return props.node.icon ? props.node.icon : icon;
};

const mouseOver = (): void =>
{
    hover.value = true;
};

const mouseOut = (): void =>
{
    if (contextMenu.value == false)
    {
        hover.value = false;
    }
};

const togglePopover = async (): Promise<void> =>
{
    if (contextMenu.value == false)
    {
        $events.$emit('tree::popover::hide', props.node);
    }

    contextMenu.value = !contextMenu.value;

    if (contextMenu.value)
    {
        menuItems.value = await getMenu(props.node);

        if (menuItems.value.length === 0)
        {
            $alert.info($t('[[[Ten element nie zawiera menu kontekstowego.]]]'));
            contextMenu.value = false;
        }
    }
};

const onMenuItem = (node: TreeNode, item: any, confirmed: boolean): void =>
{
    if (confirmed)
    {
        $events.$emit('tree::popover::hide', node);
        togglePopover();
        item.click(node);
    }
};

const onToggle = (node: TreeNode): void =>
{
    emit('toggle', node);
};

const onClick = (node: TreeNode): void =>
{
    emit('click', node);
};

const onSort = (pages: TreeNode[]): void =>
{
    emit('sort', pages);
};
</script>

<template>
    <li :id="container" :class="{ 'root-node': node.root }" @mouseover.stop="mouseOver" @mouseout.stop="mouseOut">
        <span class="d-flex align-items-center" :class="{ 'active': isActive, 'muted': typeof node.isActive === 'boolean' && !node.isActive }">
            <slot name="toggle" :node="node">
                <i v-if="!hasChildren" class="toggle fa" />
                <i v-if="isCollapsed" class="toggle fas fa-caret-down" @click="onToggle(node)" />
                <i v-if="isExpanded" class="toggle fas fa-caret-up" @click="onToggle(node)" />
            </slot>
            <slot name="icon" :node="node">
                <i v-if="!hasChildren" :class="icon(`fas fa-fw fa-folder`)" />
                <i v-if="isCollapsed" :class="icon(`fas fa-fw fa-folder`)" />
                <i v-if="isExpanded" :class="icon(`fas fa-fw fa-folder-open`)" />
            </slot>
            <slot name="default" :node="node">
                <a href="#" class="flex-fill node-title" :title="node.name" @click.prevent="onClick(node)">{{ node.name }}<span v-if="node.counter >= 0"> [{{ node.counter }}]</span></a>
            </slot>

            <IdeoDropdown
                v-if="hasMenu"
                variant="link"
                no-caret
                size="sm"
                :visibility-hidden="!(hover || media.mobile())"
                @toggle="togglePopover"
                @hidden="hover = false"
            >
                <template #button-content>
                    <i class="fa fa-ellipsis-vertical" />
                </template>

                <template v-for="(item, index) in menuItems" :key="index">
                    <IdeoDropdownDivider v-if="item.divider" />
                    <IdeoDropdownItemButton
                        v-else
                        :message="item.confirm"
                        :message-placement="item.confirmPlacement"
                        :value="item"
                        @confirm="onMenuItem(node, item, true)"
                        @click.stop="onMenuItem(node, item, !item.confirm)"
                    >
                        <i :class="item.icon" />
                        <span>{{ item.name }}</span>
                    </IdeoDropdownItemButton>
                </template>
            </IdeoDropdown>
        </span>
        <IdeoTreeNodes
            :pages="pages"
            :expanded="expanded"
            :parent="node"
            :menu="menu"
            :active="active"
            :move="move"
            :disabled="disabled"
            @toggle="onToggle"
            @click="onClick"
            @sort="onSort"
        >
            <template #toggle="args: any">
                <slot name="toggle" :node="args.node" />
            </template>
            <template #icon="args: any">
                <slot name="icon" :node="args.node" />
            </template>
            <template #default="args: any">
                <slot name="default" :node="args.node" />
            </template>
        </IdeoTreeNodes>

        <!-- <ideo-popover ref="popover" :show="contextMenu" :target="target" :container="container" placement="bottomright" :local="true">
            <div class="dropdown">
                <div class="dropdown-menu">
                    <template v-for="(item, index) in menuItems" :key="index">
                        <div class="dropdown-divider" v-if="item.divider"></div>
                        <button type="button" :id="menuItemId(index)" class="dropdown-item" :class="item.class" @click="onMenuItem(node, item, !item.confirm)" v-else>
                            <i class="me-2 icon" :class="item.icon"></i> {{ item.name }}
                            <confirmation v-if="item.confirm" :target="menuItemId(index)" :message="item.confirm" :value="item" @confirm="" :placement="item.confirmPlacement || 'right'" />
                        </button>
                    </template>
                </div>
            </div>
        </ideo-popover> -->
    </li>
</template>

<style lang="scss" scoped>
.root-node {
    .fas:not(.toggle) {
        color: var(--bs-primary);
    }

    .active a, a {
        color: var(--bs-primary);
    }
}
</style>
